import { groupBy, orderBy, uniqBy } from 'lodash';

import {
  CandidatesWithPosition,
  IPositionCandidate,
  IPositionWithCandidates,
} from '@types';
import { ICandidatesViewState, isDefaultSort } from '@hooks';
import { CandidateListingStatusLabel, CandidateStatus } from '@constants';
import {
  formatPositionTitle,
  normalizeCandidateStatus,
  sortCandidateGroups,
  sortListCandidates,
} from '@utils';
import { CandidateSortKey } from './columns';

export type GroupedRows<T> = {
  id: string;
  name: string;
  candidates: T[];
  section?: string;
  workstream?: string | null;
};

export type GroupedCandidates = GroupedRows<CandidatesWithPosition>;

export type CandidateLookups = {
  positionTitlesById: Map<string, string[]>;
  workstreamsById: Map<string, string[]>;
};

const collectByCandidate = (
  positions: IPositionWithCandidates[],
  read: (position: IPositionWithCandidates) => string,
): Map<string, string[]> => {
  const map = new Map<string, string[]>();

  positions.forEach((position) => {
    const value = read(position);
    if (!value) return;

    position.candidates.forEach((candidate) => {
      const collected = map.get(candidate.id) ?? [];
      if (!collected.includes(value)) collected.push(value);
      map.set(candidate.id, collected);
    });
  });

  return map;
};

export const buildCandidateLookups = (
  data: IPositionWithCandidates[],
): CandidateLookups => ({
  positionTitlesById: collectByCandidate(data, formatPositionTitle),
  workstreamsById: collectByCandidate(
    data,
    (position) => position.workstream ?? '',
  ),
});

export const buildCandidateGroups = (
  data: IPositionWithCandidates[],
  view: ICandidatesViewState,
  { positionTitlesById, workstreamsById }: CandidateLookups,
): GroupedCandidates[] => {
  const sortKey = view.sort.key as CandidateSortKey;
  const { direction } = view.sort;

  const byLookup =
    (map: Map<string, string[]>) => (candidates: CandidatesWithPosition[]) =>
      orderBy(
        candidates,
        (candidate) => (map.get(candidate.id)?.[0] ?? '').toLowerCase(),
        [direction],
      );

  const sorted = (candidates: CandidatesWithPosition[]) => {
    if (sortKey === 'workstream') return byLookup(workstreamsById)(candidates);
    if (sortKey === 'position') return byLookup(positionTitlesById)(candidates);

    return sortListCandidates(
      candidates,
      sortKey as keyof IPositionCandidate,
      direction,
      { floatStarred: isDefaultSort(view.sort) },
    );
  };

  if (view.groupBy === 'workstream-position') {
    return [...data]
      .sort((a, b) => (a.workstream ?? '').localeCompare(b.workstream ?? ''))
      .map(({ candidates, ...position }) => ({
        id: position.id,
        name: position.name,
        workstream: position.workstream,
        section: position.workstream ?? 'No workstream',
        candidates: sorted(
          uniqBy(candidates, 'id') as CandidatesWithPosition[],
        ),
      }))
      .filter((group) => group.candidates.length);
  }

  if (view.groupBy === 'position') {
    return data
      .map(({ candidates, ...position }) => ({
        id: position.id,
        name: position.name,
        workstream: position.workstream,
        candidates: sorted(
          uniqBy(candidates, 'id') as CandidatesWithPosition[],
        ),
      }))
      .filter((group) => group.candidates.length);
  }

  const allCandidates = uniqBy(
    data.flatMap(({ candidates }) => candidates),
    'id',
  ) as CandidatesWithPosition[];

  if (view.groupBy === 'workstream') {
    return Object.entries(
      groupBy(
        allCandidates,
        (candidate) =>
          workstreamsById.get(candidate.id)?.[0] || 'No workstream',
      ),
    ).map(([name, candidates]) => ({
      id: name,
      name,
      candidates: sorted(candidates),
    }));
  }

  if (view.groupBy === 'none') {
    return [{ id: '', name: '', candidates: sorted(allCandidates) }];
  }

  return sortCandidateGroups(
    Object.entries(groupBy(allCandidates, view.groupBy)),
    view.groupBy,
  ).map(([key, candidates]) => ({
    id: key,
    name:
      view.groupBy === 'status'
        ? CandidateListingStatusLabel[
            (normalizeCandidateStatus(key) ?? key) as CandidateStatus
          ] ?? key
        : key,
    candidates: sorted(candidates as CandidatesWithPosition[]),
  }));
};

export const formatGroupName = (name: string) => name.split(',').join(', ');
